import {
  TableHeadCustom,
  TableEmptyRows,
  TableNoData,
  emptyRows,
  useTable,
} from "../../../components/table";
import ProductTableRow from "./ProductTableRow";
import { IProduct, ProductStatus } from "../../../@types/product";
import { Brands } from "../../../@types/brand.type";
import { ProductService } from "../../../api/product.service";
import { useEffect, useState } from "react";
import { Dialog, DialogTitle, Table, TableBody } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import ProductNewEditForm from "./ProductAddEditForm";
import StockAdjustmentDialog from "../stock/StockAdjustmentDialog";
import useBarcodeScanner from "../../../hooks/useBarcodeScanner";

type Props = {
  filterName: string;
  filterBrandId: string;
  filterCategory: string;
  filterSupplierId: string;
  brands: Brands[];
};

export default function ProductList({
  filterName,
  filterBrandId,
  filterCategory,
  filterSupplierId,
  brands,
}: Props) {
  const getErrorMessage = (error: any): string => {
    const apiMessage = error?.response?.data?.message;
    if (Array.isArray(apiMessage)) return apiMessage[0];
    if (typeof apiMessage === "string" && apiMessage.trim()) return apiMessage;

    if (Array.isArray(error?.message)) return error.message[0];
    if (typeof error?.message === "string" && error.message.trim())
      return error.message;

    return "Something went wrong";
  };

  const [products, setProducts] = useState<IProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<IProduct>();
  const [openFormProduct, setOpenFormProduct] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<boolean>(false);
  const [openAdjustment, setOpenAdjustment] = useState(false);
  const [selectedProductForAdjustment, setSelectedProductForAdjustment] = useState<IProduct | null>(null);

  useBarcodeScanner({
    onScan: (barcode) => {
      if (openFormProduct || openAdjustment) return;

      const product = products.find(
        (p) => p.code?.toLowerCase() === barcode.toLowerCase()
      );

      if (product) {
        setSelectedProduct(product);
        setViewMode(true);
        setOpenFormProduct(true);
        enqueueSnackbar(`Opened ${product.name}`, { variant: "success" });
      } else {
        enqueueSnackbar(`Product with code ${barcode} not found`, {
          variant: "warning",
        });
      }
    },
  });

  const getAllProducts = async () => {
    const productService = new ProductService();
    try {
      const { data } = await productService.getActiveProducts();
      setProducts(data);
    } catch (error) {
      console.error("Error", error);
    }
  };
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    selected,
    onSelectAllRows,
    onSort,
  } = useTable({ defaultOrderBy: "startTime" });

  const TABLE_HEAD = [
    { id: "name", label: "Product", align: "center" },
    { id: "createdAt", label: "Created At", align: "center" },
    { id: "currentStock", label: "Stock", align: "center" },
    { id: "price", label: "Price", align: "center" },
    { id: "brandSupplier", label: "Brand / Supplier", align: "center", disableSort: true },
    { id: "status", label: "Status", align: "center" },
    { id: "actions", label: "", align: "right", disableSort: true },
  ];

  useEffect(() => {
    getAllProducts();
  }, []);

  const handleDeleteProduct = async (id: string) => {
    try {
      const productService = new ProductService();
      await productService.deleteProduct(id);
      getAllProducts();
      enqueueSnackbar("Product Deleted Successfully!", {
        variant: "success",
      });
    } catch (error: any) {
      enqueueSnackbar(getErrorMessage(error), {
        variant: "error",
      });
    }
  };

  const handlePublishProduct = async (id: string, isActive: boolean) => {
    try {
      const productService = new ProductService();
      await productService.updateActiveStatus(id);
      getAllProducts();
      enqueueSnackbar(
        `Product ${isActive ? "Deactivated" : "Activated"
        } Successfully!`,
        {
          variant: "success",
        }
      );
    } catch (error: any) {
      enqueueSnackbar(getErrorMessage(error), {
        variant: "error",
      });
    }
  };

  const handleAdjustStock = (product: IProduct) => {
    setSelectedProductForAdjustment(product);
    setOpenAdjustment(true);
  };

  const dataFiltered = products.filter((product) => {
    const matchesName = product.name
      .toLowerCase()
      .includes(filterName.toLowerCase());

    const selectedBrandName = brands
      .find((b) => String(b.id) === String(filterBrandId))
      ?.name?.toLowerCase();

    const productBrandName = String(
      (product as any)?.brand?.name ??
        (product as any)?.productDetails?.brandName ??
        ""
    ).toLowerCase();

    const matchesBrand = filterBrandId
      ? String((product as any)?.brandId ?? (product as any)?.brand?.id ?? "") ===
          filterBrandId || (selectedBrandName ? productBrandName === selectedBrandName : false)
      : true;

    const matchesCategory = filterCategory
      ? product.category === filterCategory
      : true;

    const matchesSupplier = filterSupplierId
      ? String(
          (product as any)?.supplierId ?? (product as any)?.supplier?.id ?? ""
        ) === filterSupplierId
      : true;

    return matchesName && matchesBrand && matchesCategory && matchesSupplier;
  });

  return (
    <>
      <Table size={dense ? "small" : "medium"} sx={{ minWidth: 800 }}>
        <TableHeadCustom
          order={order}
          orderBy={orderBy}
          headLabel={TABLE_HEAD}
          rowCount={dataFiltered.length}
          numSelected={selected.length}
          onSort={onSort}
          onSelectAllRows={(checked) =>
            onSelectAllRows(
              checked,
              dataFiltered.map((row: any) => row.id)
            )
          }
        />
        <TableBody>
          {dataFiltered.map((product, key) => (
            <ProductTableRow
              product={product}
              key={key}
              onDelete={() => handleDeleteProduct(product.id as string)}
              onPublish={() =>
                handlePublishProduct(
                  product.id as string,
                  product.isActive || false
                )
              }
              onEdit={() => {
                setSelectedProduct(product);
                setViewMode(false);
                setOpenFormProduct(true);
              }}
              onView={() => {
                setSelectedProduct(product);
                setViewMode(true);
                setOpenFormProduct(true);
              }}
              onAdjustStock={() => handleAdjustStock(product)}
            />
          ))}
          <TableEmptyRows
            height={63}
            emptyRows={emptyRows(page, rowsPerPage, dataFiltered.length)}
          />
          {!dataFiltered.length && (
            <TableNoData isNotFound={dataFiltered.length <= 0} />
          )}
        </TableBody>
      </Table>
      <Dialog
        fullWidth
        maxWidth="md"
        open={openFormProduct}
        onClose={() => setOpenFormProduct(false)}>
        <DialogTitle>{viewMode ? "View" : "Edit"} Product</DialogTitle>
        <ProductNewEditForm
          onCancel={(value?: boolean) => {
            setOpenFormProduct(false);

            setSelectedProduct(undefined);
            if (value) getAllProducts();
            setViewMode(false);
          }}
          isEdit={!viewMode}
          isView={viewMode}
          selectedProduct={selectedProduct}
        />
      </Dialog>
      <StockAdjustmentDialog
        open={openAdjustment}
        onClose={() => setOpenAdjustment(false)}
        product={
          selectedProductForAdjustment
            ? {
              id: selectedProductForAdjustment.id,
              name: selectedProductForAdjustment.name,
              currentStock: Number(selectedProductForAdjustment.currentStock || 0),
            }
            : null
        }
        onSuccess={() => {
          getAllProducts();
          setOpenAdjustment(false);
        }}
      />
    </>
  );
}
